import { useState, useMemo, useEffect } from 'react';
import { Box, Button, Typography, TextField, Grid, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Delete, Add } from '@mui/icons-material';
import { Page, PageTitleWithBack, Table, Pagination, Loader } from '../../../components';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { IPlanStyle } from '../../../models';
import { getPlanStyles, deletePlanStyle, addPlanStyle } from '../../../fetch';

export const ManagePlanStyles = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [planStyles, setPlanStyles] = useState<IPlanStyle[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [isDeleting, setDeleting] = useState(false);
  const [isAdding, setAdding] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [planStyleValue, setPlanStyleValue] = useState('');
  const [sageDescriptionValue, setSageDescriptionValue] = useState('');

  const fetchPlanStyles = async () => {
    setLoading(true);
    try {
      const res = await getPlanStyles({
        page: page + 1,
        perPage,
        sortBy: 'Name'
      });
      setPlanStyles(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(`Error loading plan styles, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanStyles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const handleAdd = async () => {
    try {
      setAdding(true);
      await addPlanStyle({
        name: planStyleValue,
        sageDescription: sageDescriptionValue
      });
      enqueueSnackbar('Plan Style added!', {
        variant: 'success'
      });
      // reset the input
      setPlanStyleValue('');
      setSageDescriptionValue('');
      fetchPlanStyles();
    } catch (error) {
      enqueueSnackbar('Error adding plan style, please refresh and try again.', {
        variant: 'error'
      });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    const result = window.confirm('Are you sure you want to delete this plan style?');
    if (result) {
      setDeleting(true);
      try {
        await deletePlanStyle(id);
        enqueueSnackbar(`Plan Style deleted!`, {
          variant: 'success'
        });
        fetchPlanStyles();
      } catch (error) {
        enqueueSnackbar('Error deleting plan style, please refresh and try again.', {
          variant: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Sage Description',
        accessor: 'sageDescription',
        sort: false
      },
      {
        Header: 'Job Type Code',
        accessor: 'sageJobTypeCode',
        sort: false
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 300,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPlanStyle } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                id="delete"
                className={classes.deleteButton}
                startIcon={<Delete />}
                onClick={() => handleDelete(original.planStyleId)}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {(isAdding || isDeleting) && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Manage Plan Styles">
        <PageTitleWithBack
          backText="Back to Admin home"
          backUrl="/admin"
          title="Manage Plan Styles"
          subTitle="Use this page to manage the items that show up in the Plan Styles dropdown list"
        />
        <Box marginLeft="1rem">
          <Typography variant="h6">Add Plan Style</Typography>
          <Grid container alignItems="center" spacing={1}>
            <Grid xs={12} sm={4} item>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                autoComplete="nope"
                label="Name"
                name="name"
                inputProps={{
                  maxLength: 100
                }}
                value={planStyleValue}
                onChange={e => setPlanStyleValue(e.target.value)}
              />
            </Grid>
            <Grid xs={12} sm={4} item>
              <Tooltip title="You must start the Sage description with 'Temp' for it to be included on the Price Sheets" placement="right-end">
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  autoComplete="nope"
                  label="Sage Description"
                  name="sageDescription"
                  inputProps={{
                      maxLength: 500
                  }}
                  value={sageDescriptionValue}
                  onChange={e => setSageDescriptionValue(e.target.value)}
                />
              </Tooltip>
            </Grid>
            <Grid xs={12} sm={4} item>
              <Button
                color="secondary"
                startIcon={<Add />}
                disabled={!planStyleValue}
                variant="contained"
                onClick={handleAdd}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Box>
        <Box marginTop="1rem">
          <Table columns={columns} data={planStyles} isLoading={isLoading} hidePagination />
          {!isLoading && (
            <Pagination
              page={page}
              count={recordCount}
              rowsPerPage={perPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </Box>
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    marginTop: theme.spacing(0.5),
    position: 'relative'
  },
  actionButtons: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  subTitle: {
    display: 'block',
    color: '#000',
    marginTop: 5
  }
}));
